import { truncate } from "lodash";
import { markdown } from "../../../helpers/markdown";

/**
 * Helpers that build compact, plain-text previews of property values for the
 * App Creator table layout (`ItemsTableView`).
 *
 * Restores the truncation the legacy Backgrid table applied per cell type
 * (see `app/assets/javascripts/app_creator/views/backgrid_cells.js.coffee`
 * before the React migration in #4010), which was lost when rendering moved to
 * the shared `PropertyShow` component. Without it, long multiline-text and HTML
 * values blow up the table rows (#4978).
 */

/**
 * Strip HTML tags from a string and return collapsed, trimmed plain text.
 */
export function htmlToPlainText(html: string | null | undefined): string {
  if (typeof html !== "string" || html === "") return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
}

/**
 * Multiline-text cell preview: render markdown, drop the formatting and
 * truncate to a word boundary (legacy `TruncatingTextCell`, 150 chars).
 */
export function previewMarkdown(
  value: string | null | undefined,
  length = 150,
): string {
  return truncate(htmlToPlainText(markdown(value)), {
    length,
    separator: /\s/,
  });
}

/**
 * HTML cell preview: strip tags and truncate to a word boundary (legacy
 * `HtmlPropertyCell`, 420 chars). Callers pass already-unescaped HTML.
 */
export function previewHtml(
  value: string | null | undefined,
  length = 420,
): string {
  return truncate(htmlToPlainText(value), { length, separator: /\s/ });
}

/**
 * Short scalar cell preview for email/url/address/file values (legacy
 * `_.truncate(..., { length: 25 })`).
 */
export function previewText(
  value: string | null | undefined,
  length = 25,
): string {
  return truncate(String(value ?? ""), { length });
}
